import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Clock, Mail, Phone, Calendar, ShoppingCart, MessageCircle, X } from 'lucide-react';
import SeoHead from './SeoHead';
import FAQSection from './FAQSection';

const faqItems = [
  { q: 'Do you offer team building in Pretoria and Gauteng?', a: 'Yes, we run team building activities in Pretoria, Centurion, Midrand, Johannesburg, and nearby Gauteng locations.' },
  { q: 'Can team building include catering?', a: 'Yes, we can combine activities with catering and other event support services.' },
  { q: 'What group sizes do you support?', a: 'We support small groups and larger corporate teams depending on activity format.' },
  { q: 'How do we request a quote near me?', a: 'Complete the form and send directly to WhatsApp for quick response.' },
  { q: 'How soon should we book?', a: 'Weekend and peak dates fill fast, so early booking is recommended.' },
];

const TeamBuilding = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    eventDate: '',
    groupSize: '',
    activities: [],
    additionalNotes: ''
  });

  const activities = [
    {
      id: 'quad-biking',
      name: 'Quad Biking',
      description: 'Adventure through scenic trails with our guided quad biking experience',
      icon: '🏍️',
      price: 300,
      priceLabel: 'R300 per person',
      duration: '2-3 hours',
      minGroup: 8,
      maxGroup: 50
    },
    {
      id: 'obstacle-course',
      name: 'Obstacle Course Challenges',
      description: 'Test teamwork and problem-solving skills with our custom obstacle courses',
      icon: '🎯',
      price: 200,
      priceLabel: 'R200 per person',
      duration: '1-2 hours',
      minGroup: 6,
      maxGroup: 30
    },
    {
      id: 'outdoor-picnics',
      name: 'Outdoor Picnics & Games',
      description: 'Relaxed team bonding with outdoor games and delicious picnic spreads',
      icon: '🧺',
      price: 150,
      priceLabel: 'R150 per person',
      duration: '3-4 hours',
      minGroup: 10,
      maxGroup: 100
    },
    {
      id: 'spit-braai',
      name: 'Spit Braai Lunches',
      description: 'Authentic South African braai experience with team cooking activities',
      icon: '🔥',
      price: 250,
      priceLabel: 'R250 per person',
      duration: '2-3 hours',
      minGroup: 8,
      maxGroup: 60
    },
    {
      id: 'custom-packages',
      name: 'Customized Corporate Packages',
      description: 'Tailored team building experiences designed for your specific objectives',
      icon: '🎨',
      price: 350,
      priceLabel: 'From R350 per person',
      duration: 'Flexible',
      minGroup: 5,
      maxGroup: 200
    }
  ];

  useEffect(() => {
    const saved = localStorage.getItem('ubuntu-events-management-teambuilding-items');
    if (saved) {
      setSelectedItems(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('ubuntu-events-management-teambuilding-items', JSON.stringify(selectedItems));
  }, [selectedItems]);

  const addToCart = (activity) => {
    const existingItem = selectedItems.find((item) => item.id === activity.id);
    if (existingItem) {
      setSelectedItems(
        selectedItems.map((item) =>
          item.id === activity.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setSelectedItems([
        ...selectedItems,
        {
          id: activity.id,
          name: activity.name,
          price: activity.price,
          priceLabel: activity.priceLabel,
          quantity: 1,
        },
      ]);
    }
  };

  const removeFromCart = (itemId) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    const existingItem = selectedItems.find((item) => item.id === itemId);
    if (existingItem) {
      setSelectedItems(
        selectedItems.map((item) =>
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        )
      );
      return;
    }

    const activity = activities.find((a) => a.id === itemId);
    if (!activity) return;

    setSelectedItems([
      ...selectedItems,
      {
        id: activity.id,
        name: activity.name,
        price: activity.price,
        priceLabel: activity.priceLabel,
        quantity: newQuantity,
      },
    ]);
  };

  const getQuantity = (activityId) => selectedItems.find((item) => item.id === activityId)?.quantity ?? 0;

  const getTotalPrice = () =>
    selectedItems.reduce((total, item) => total + item.price * item.quantity, 0);

  const buildWhatsAppListMessage = () => {
    const lines = selectedItems.map((item, idx) => {
      const lineTotal = item.price * item.quantity;
      return `${idx + 1}. ${item.name}\n   ${item.quantity} x ${item.priceLabel} = R${lineTotal.toLocaleString()}`;
    });
    return `Hi UBUNTU EVENTS MANAGEMENT,

I'd like a quote for the following team building selections:

${lines.join('\n\n')}

Estimated total: R${getTotalPrice().toLocaleString()}

Please confirm availability and next steps.`;
  };

  const sendSelectionWhatsApp = () => {
    if (!selectedItems.length) return;
    const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(buildWhatsAppListMessage())}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleInputChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleActivityToggle = (activityId) => {
    setContactForm(prev => ({
      ...prev,
      activities: prev.activities.includes(activityId)
        ? prev.activities.filter(id => id !== activityId)
        : [...prev.activities, activityId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message
    const selectedActivities = activities
      .filter(activity => contactForm.activities.includes(activity.id))
      .map(activity => activity.name)
      .join(', ');
    const selectedCartActivities = selectedItems
      .map((item) => `${item.name} (${item.quantity}x)`)
      .join(', ');

    const message = `Hi! I'm interested in booking a team building event:

*Contact Details:*
Name: ${contactForm.name}
Company: ${contactForm.company}
Email: ${contactForm.email}
Phone: ${contactForm.phone}

*Event Details:*
Date: ${contactForm.eventDate}
Group Size: ${contactForm.groupSize}
Selected Activities: ${selectedActivities || 'To be discussed'}
Selected Cart Activities: ${selectedCartActivities || 'None selected'}
Estimated Cart Total: ${selectedItems.length ? `R${getTotalPrice().toLocaleString()}` : 'Not calculated'}

*Additional Notes:*
${contactForm.additionalNotes}

Please contact me to discuss pricing and availability.`;

    const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-cream to-white ${selectedItems.length > 0 ? 'pb-32 sm:pb-28' : ''}`}>
      <SeoHead
        title="Team Building Pretoria | Ubuntu Enterprises"
        description="Team building in Pretoria and Gauteng with outdoor activities, corporate packages, and WhatsApp booking."
        canonicalPath="/teambuilding"
        faqSchema={faqItems}
      />
      {/* Hero Banner */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image - Quad biking in South African landscape */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('/team-building-bg.png')"
          }}
        />
        
        {/* Overlay Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-playfair font-bold mb-6"
          >
            Unite, Compete & Celebrate
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-8 font-light"
          >
            Transform your team through exciting outdoor adventures and authentic South African experiences
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => document.getElementById('activities').scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary text-lg px-8 py-4"
            >
              Explore Activities
            </button>
            <button
              onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}
              className="btn-secondary text-lg px-8 py-4"
            >
              Book Your Event
            </button>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-8">
              Building Stronger Teams Through Adventure
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                At UBUNTU EVENTS MANAGEMENT, we believe that the best teams are built through shared experiences, 
                challenges, and celebrations. Our team building programs combine the thrill of outdoor 
                adventures with the warmth of South African hospitality, creating unforgettable moments 
                that strengthen bonds and boost morale.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mt-12">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <div className="text-4xl mb-4">🤝</div>
                  <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-3">
                    Team Bonding
                  </h3>
                  <p className="text-gray-600">
                    Strengthen relationships and build trust through collaborative challenges
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <div className="text-4xl mb-4">🏆</div>
                  <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-3">
                    Leadership Development
                  </h3>
                  <p className="text-gray-600">
                    Develop leadership skills and problem-solving abilities in dynamic environments
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <div className="text-4xl mb-4">🎉</div>
                  <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-3">
                    Fun & Celebration
                  </h3>
                  <p className="text-gray-600">
                    Create lasting memories while celebrating achievements and milestones
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Activities Section */}
      <section id="activities" className="py-20 px-4 bg-gradient-to-r from-gold/5 to-olive/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-8">
              Our Team Building Activities
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose from our exciting range of activities, each designed to challenge, inspire, and unite your team
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activities.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="text-center">
                  <div className="text-6xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {activity.icon}
                  </div>
                  <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-4">
                    {activity.name}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {activity.description}
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>Duration: {activity.duration}</span>
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-500">
                      <Users className="w-4 h-4 mr-2" />
                      <span>Group: {activity.minGroup}-{activity.maxGroup} people</span>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-gold/10 to-olive/10 p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-700 font-medium">
                      Perfect for building communication, trust, and teamwork
                    </p>
                  </div>

                  <div className="text-lg font-bold text-gold mb-3">{activity.priceLabel}</div>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => updateQuantity(activity.id, getQuantity(activity.id) - 1)}
                      className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300"
                      disabled={getQuantity(activity.id) === 0}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={getQuantity(activity.id)}
                      onChange={(e) => {
                        const next = Math.max(0, parseInt(e.target.value, 10) || 0);
                        updateQuantity(activity.id, next);
                      }}
                      className="w-14 h-8 text-center font-semibold border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-gold focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => addToCart(activity)}
                      className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300"
                    >
                      +
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => addToCart(activity)}
                    className="w-full btn-primary text-sm py-2"
                  >
                    {getQuantity(activity.id) > 0 ? 'Add More' : 'Select Activity'}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-8">
              Book Your Team Event
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ready to create an unforgettable team building experience? Get in touch with us to discuss 
              your requirements and receive a customized proposal.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="card max-w-2xl mx-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    value={contactForm.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Event Date *
                  </label>
                  <input
                    type="date"
                    value={contactForm.eventDate}
                    onChange={(e) => handleInputChange('eventDate', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Size *
                  </label>
                  <select
                    value={contactForm.groupSize}
                    onChange={(e) => handleInputChange('groupSize', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    required
                  >
                    <option value="">Select group size</option>
                    <option value="5-10">5-10 people</option>
                    <option value="11-20">11-20 people</option>
                    <option value="21-50">21-50 people</option>
                    <option value="51-100">51-100 people</option>
                    <option value="100+">100+ people</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Activities of Interest
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activities.map(activity => (
                    <label key={activity.id} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={contactForm.activities.includes(activity.id)}
                        onChange={() => handleActivityToggle(activity.id)}
                        className="w-5 h-5 text-gold focus:ring-gold border-gray-300 rounded"
                      />
                      <span className="text-gray-700">{activity.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes or Special Requirements
                </label>
                <textarea
                  value={contactForm.additionalNotes}
                  onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                  rows="4"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  placeholder="Tell us about your team's goals, any specific requirements, or questions you might have..."
                />
              </div>

              <button
                type="submit"
                className="btn-primary text-lg px-8 py-4 w-full"
              >
                <Phone className="w-5 h-5 inline mr-2" />
                Send via WhatsApp
              </button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div className="card text-center">
                <Phone className="w-8 h-8 text-gold mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Call Us</h3>
                <p className="text-gray-600">066 328 6231</p>
              </div>
              <div className="card text-center">
                <Mail className="w-8 h-8 text-gold mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Email Us</h3>
                <p className="text-gray-600">info@ubuntueventsmanagement.co.za</p>
              </div>
              <div className="card text-center">
                <Calendar className="w-8 h-8 text-gold mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Book Now</h3>
                <p className="text-gray-600">Available 7 days a week</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {selectedItems.length > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/15 bg-[#f5f0e6]/95 backdrop-blur-sm shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowCart(true)}
              className="flex items-center gap-3 text-left text-black min-w-0 flex-1"
            >
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2a2a2a] text-[#f5f0e6]">
                <ShoppingCart size={20} />
                <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                  {selectedItems.length}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-black">
                  {selectedItems.length} selection{selectedItems.length !== 1 ? 's' : ''} · R{getTotalPrice().toLocaleString()}
                </span>
                <span className="block text-xs text-black/55 truncate">
                  Tap to review, edit quantities, or send the full list to WhatsApp
                </span>
              </span>
            </button>
            <div className="flex gap-2 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowCart(true)}
                className="flex-1 sm:flex-none rounded-lg border border-black/20 bg-white/60 px-4 py-2.5 text-sm font-semibold text-black hover:bg-white/90"
              >
                Review
              </button>
              <button
                type="button"
                onClick={sendSelectionWhatsApp}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
              >
                <MessageCircle size={18} />
                WhatsApp
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {showCart && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#f5f0e6] rounded-t-2xl sm:rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-black/10 shadow-xl"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-black">Your team building selection</h3>
              <button
                type="button"
                onClick={() => setShowCart(false)}
                className="text-black/50 hover:text-black"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {selectedItems.map((item) => (
                <div key={item.id} className="flex justify-between items-center border-b border-black/10 pb-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-black">{item.name}</h4>
                    <p className="text-xs text-black/60">{item.priceLabel}</p>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center text-sm text-black"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={(e) => {
                        const next = Math.max(0, parseInt(e.target.value, 10) || 0);
                        updateQuantity(item.id, next);
                      }}
                      className="w-12 h-7 text-center text-sm font-medium border border-black/20 rounded bg-white/80 focus:ring-2 focus:ring-gold focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center text-sm text-black"
                    >
                      +
                    </button>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-semibold text-sm">R{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-black/10 bg-white/40 p-3 mb-4 max-h-40 overflow-y-auto text-xs text-black/80 whitespace-pre-wrap font-mono leading-relaxed">
              {buildWhatsAppListMessage()}
            </div>

            <div className="border-t border-black/15 pt-4 mb-4">
              <div className="flex justify-between text-lg font-bold text-black">
                <span>Total:</span>
                <span>R{getTotalPrice().toLocaleString()}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={sendSelectionWhatsApp}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center space-x-2"
            >
              <MessageCircle size={20} />
              <span>Send full list to WhatsApp</span>
            </button>
          </motion.div>
        </div>
      )}
      <FAQSection title="Team Building FAQs" faqs={faqItems} />
    </div>
  );
};

export default TeamBuilding;
